import axios, { AxiosResponse } from "axios";
import firebase from "firebase/app";
import "firebase/auth";
import {
  tAcceptConnectionRequestArgs,
  tAddPromotionBody,
  tApplication,
  tApplicationAcceptBody,
  tBrand,
  tCreateConnectionRequestArgs,
  tCreateDraftBody,
  tCreatePromotionBody,
  tDeclineOrderBody,
  tEditOrderBody,
  tMessage,
  tProduct,
  tRep,
  tSetConnectionStatusArgs,
  tUpdateConnectionArgs,
  tUpdatePromotionBody,
  tUser,
} from "tmt_api/src/types/types";
import { API_URL } from "../env";

// #region POST requests

type tApplicationsEndpoint = {
  endpoint: "/applications";
  body: tApplication;
};
type tLoginRedirectEndpoint = {
  endpoint: "/login/redirect";
  body: { customerId?: number };
};
type tUploadProduct = {
  endpoint: "/products/upload";
  body: { productSheetId: string };
};
type tExportProduct = {
  endpoint: "/products/export";
  body?: undefined;
};
type tExportOrdersReport = {
  endpoint: "/report/orders";
  body: { start?: string; end?: string };
};
type tDeleteProduct = {
  endpoint: "/products/delete";
  body: tProduct;
};
type tCheckEmailVerificationEndpoint = {
  endpoint: "/users/checkVerification";
  body: { email: string };
};
type tVerifyEmailUpdatePassword = {
  endpoint: `/users/verify`;
  body: { email: string; password: string };
};
type tMessageEndpoint = {
  endpoint: "/messages";
  body: { myNewMessages: tMessage[]; myThreadLocations: string[]; mySenderRefPath: string };
};
type tCheckoutAll = {
  endpoint: "/checkout";
  body: undefined;
};
type tCheckoutOne = {
  endpoint: `/checkout/${string}`;
  body: undefined;
};
type tUpdateProductToCart = {
  endpoint: `/cart/${string}/${string}/${string}/${string}/add`;
  body:
    | {
        addTester?: boolean;
      }
    | undefined;
};
type tUpdatePONumber = {
  endpoint: `/cart/${string}/PO`;
  body: { pONumber: string };
};
type tRemoveDraft = {
  endpoint: `/cart/${string}/removeDraft`;
  body: undefined;
};
type tOrderAll = {
  endpoint: `/orders/${string}`;
  body: undefined;
};
type tDeleteProductFromCart = {
  endpoint: `/cart/${string}/${string}/${string}/remove`;
  body?: undefined;
};
type tOrderOne = {
  endpoint: `/orders/${string}/${string}`;
  body: undefined;
};
type tRetailerSetConnectionRequest = {
  endpoint: `/brands/${string}/connections/${string}/set`;
  body: tCreateConnectionRequestArgs;
};
type tRetailerDeleteConnectionRequest = {
  endpoint: `/brands/${string}/connections/${string}/delete`;
  body: undefined;
};
type tCreateDraft = {
  endpoint: `/cart/createDraft`;
  body: tCreateDraftBody;
};
type tDeleteDraft = {
  endpoint: `/cart/${string}/deleteDraft`;
  body: undefined;
};
type tCreateFromDraft = {
  endpoint: `/cart/${string}/create`;
  body: undefined;
};

type tAddPromoCode = {
  endpoint: `/cart/${string}/promoCode`;
  body: tAddPromotionBody;
};
type tCreatePromotion = {
  endpoint: "/promotions/create";
  body: tCreatePromotionBody;
};

type tAdminUserUpdate = {
  endpoint: "/admin/userUpdate";
  body: tUser;
};
type tAdminUserUpdateNoEmail = {
  endpoint: "/admin/userUpdateNoEmail";
  body: tUser;
};

type tPostEndpoint =
  | tUpdateProductToCart
  | tApplicationsEndpoint
  | tLoginRedirectEndpoint
  | tMessageEndpoint
  | tVerifyEmailUpdatePassword
  | tCheckEmailVerificationEndpoint
  | tCreateProductEndpoint
  | tUploadProduct
  | tExportProduct
  | tExportOrdersReport
  | tDeleteProduct
  | tCheckoutAll
  | tCheckoutOne
  | tOrderAll
  | tOrderOne
  | tDeleteProductFromCart
  | tRetailerSetConnectionRequest
  | tRetailerDeleteConnectionRequest
  | tUpdatePONumber
  | tCreateDraft
  | tCreateFromDraft
  | tAddPromoCode
  | tCreatePromotion
  | tAdminUserUpdate
  | tAdminUserUpdateNoEmail
  | tDeleteDraft
  | tRemoveDraft;

/**
 * Make an authenticated POST request to the tmt api
 *
 * @param {tPostEndpoint} endpointConfig a type-safe config that determines which endpoint to send this request to!
 * @param config additional axios config
 * @param headers additional headers to pass in the POST request
 * @returns {Promise<ApiResponse>} a promise that resolves based on the response from the api request
 */
export const post = <ApiResponse = never>(
  endpointConfig: tPostEndpoint,
  config = {},
  headers = {},
): Promise<AxiosResponse<ApiResponse>> => {
  const { endpoint, body } = endpointConfig;
  return Promise.resolve()
    .then(() => firebase.auth().currentUser?.getIdToken())
    .then((idToken) => {
      if (!idToken) {
        throw new Error("Unable to retrieve ID token for user!");
      }
      return axios.post<tPostEndpoint["body"], AxiosResponse<ApiResponse>>(`${API_URL}${endpoint}`, body, {
        ...config,
        headers: { ...headers, Authorization: `Bearer ${idToken}` },
      });
    });
};

/**
 * Make an unauthenticated POST request to the tmt api
 *
 * @param {tPostEndpoint} endpointConfig a type-safe config that determines which endpoint to send this request to!
 * @param config additional axios config
 * @param headers additional headers to pass in the POST request
 * @returns {Promise<ApiResponse>} a promise that resolves based on the response from the api request
 */
export const postNoAuth = <ApiResponse = never>(
  endpointConfig: tPostEndpoint,
  config = {},
  headers = {},
): Promise<AxiosResponse<ApiResponse>> => {
  const { endpoint, body } = endpointConfig;
  return axios.post<tPostEndpoint["body"], AxiosResponse<ApiResponse>>(`${API_URL}${endpoint}`, body, {
    ...config,
    headers: { ...headers },
  });
};

// #endregion

// #region PUT requests

type tBrandsUpdateEndpoint = {
  endpoint: `/brands/${string}/update`;
  body: tBrand;
};
type tRepsUpdateEndpoint = {
  endpoint: `/reps/${string}/update`;
  body: tRep;
};
type tUsersUpdateEndpoint = {
  endpoint: `/users/update`;
  body: tUser;
};
type tAcceptBrandConnectionsEndpoint = {
  endpoint: `/brands/${string}/connections/${string}/accept`;
  body: tAcceptConnectionRequestArgs;
};
type tDeclineBrandConnectionsEndpoint = {
  endpoint: `/brands/${string}/connections/${string}/decline`;
  body?: undefined;
};
type tCancelBrandConnectionsEndpoint = {
  endpoint: `/brands${string}/boutiques/${string}/connections/${string}/cancel`;
  body?: undefined;
};
type tBrandConnectionsStatusEndpoint = {
  endpoint: `/brands/${string}/connections/${string}/status`;
  body: tSetConnectionStatusArgs;
};
type tCreateProductEndpoint = {
  endpoint: `/products`;
  body: tProduct;
};
type tUpdateConnectionEndpoint = {
  endpoint: "/connections/update";
  body: tUpdateConnectionArgs;
};
type tAcceptApplicationsEndpoint = {
  endpoint: `/applications/${string}/accept`;
  body?: tApplicationAcceptBody;
};
type tDeclineApplicationsEndpoint = {
  endpoint: `/applications/${string}/decline`;
  body?: undefined;
};
type tUpdateProduct = {
  endpoint: `/products/update`;
  body?: tProduct;
};
type tUpdateOrder = {
  endpoint: `/orders/${string}/update`;
  body: tEditOrderBody;
};
type tDeclineOrder = {
  endpoint: `/orders/${string}/decline`;
  body: tDeclineOrderBody;
};
type tUpdatePromotion = {
  endpoint: `/promotions/${string}/update`;
  body: tUpdatePromotionBody;
};
type tMarkReadEndpoint = {
  endpoint: `/messages/read`;
  body: { senderRefPath: string; threadId: string };
};

type tPutEndpoint =
  | tBrandsUpdateEndpoint
  | tRepsUpdateEndpoint
  | tUsersUpdateEndpoint
  | tAcceptBrandConnectionsEndpoint
  | tDeclineBrandConnectionsEndpoint
  | tCancelBrandConnectionsEndpoint
  | tBrandConnectionsStatusEndpoint
  | tUpdateConnectionEndpoint
  | tAcceptApplicationsEndpoint
  | tDeclineApplicationsEndpoint
  | tUpdateProduct
  | tUpdateOrder
  | tDeclineOrder
  | tUpdatePromotion
  | tMarkReadEndpoint;

/**
 * Make an authenticated PUT request to the tmt api
 *
 * @param {tPutEndpoint} endpointConfig a type-safe config that determines which endpoint to send this request to!
 * @param config additional axios config
 * @param headers additional headers to pass in the PUT request
 * @returns {Promise<ApiResponse>} a promise that resolves based on the response from the api request
 */
export const put = <ApiResponse = never>(
  endpointConfig: tPutEndpoint,
  config = {},
  headers = {},
): Promise<AxiosResponse<ApiResponse>> => {
  const { endpoint, body } = endpointConfig;
  return Promise.resolve()
    .then(() => firebase.auth().currentUser?.getIdToken())
    .then((idToken) => {
      if (!idToken) {
        throw new Error("Unable to retrieve ID token for user!");
      }
      return axios.put<tPutEndpoint["body"], AxiosResponse<ApiResponse>>(`${API_URL}${endpoint}`, body, {
        ...config,
        headers: { ...headers, Authorization: `Bearer ${idToken}` },
      });
    });
};

type tGetBrand = {
  endpoint: `/admin/brand/${string}`;
};
type tGetRetailer = {
  endpoint: `/admin/retailer/${string}`;
};
type tGetDraft = {
  endpoint: `/cart/${string}`;
};
type tGetBrandApplication = {
  endpoint: `/admin/applications/brand/${string}`;
};

type tGetEndpoint = tGetBrand | tGetRetailer | tGetDraft | tGetBrandApplication;

/**
 * Make an authenticated GET request to the tmt api
 *
 * @param {tGetEndpoint} endpointConfig a type-safe config that determines which endpoint to send this request to!
 * @param config additional axios config
 * @param headers additional headers to pass in the DELETE request
 * @returns {Promise<ApiResponse>} a promise that resolves based on the response from the api request
 */
export const get = <ApiResponse = never>(
  endpointConfig: tGetEndpoint,
  config = {},
  headers = {},
): Promise<AxiosResponse<ApiResponse>> => {
  const { endpoint } = endpointConfig;
  return Promise.resolve()
    .then(() => firebase.auth().currentUser?.getIdToken())
    .then((idToken) => {
      if (!idToken) {
        throw new Error("Unable to retrieve ID token for user!");
      }
      return axios.get<undefined, AxiosResponse<ApiResponse>>(`${API_URL}${endpoint}`, {
        ...config,
        headers: { ...headers, Authorization: `Bearer ${idToken}` },
      });
    });
};

type tDeletePromotion = {
  endpoint: `/promotions/${string}/update`;
};

type tDeleteEndpoint = tDeletePromotion;

/**
 * Make an authenticated DELETE request to the tmt api
 *
 * @param {tDeleteEndpoint} endpointConfig a type-safe config that determines which endpoint to send this request to!
 * @param config additional axios config
 * @param headers additional headers to pass in the DELETE request
 * @returns {Promise<ApiResponse>} a promise that resolves based on the response from the api request
 */
export const deleteReq = <ApiResponse = never>(
  endpointConfig: tDeleteEndpoint,
  config = {},
  headers = {},
): Promise<AxiosResponse<ApiResponse>> => {
  const { endpoint } = endpointConfig;
  return Promise.resolve()
    .then(() => firebase.auth().currentUser?.getIdToken())
    .then((idToken) => {
      if (!idToken) {
        throw new Error("Unable to retrieve ID token for user!");
      }
      return axios.delete<undefined, AxiosResponse<ApiResponse>>(`${API_URL}${endpoint}`, {
        ...config,
        headers: { ...headers, Authorization: `Bearer ${idToken}` },
      });
    });
};

// #endregion

/**
 * tmt api interface
 * @method post -- send a post request
 * @method put -- send a put request
 */
export default {
  post,
  put,
  postNoAuth,
  get,
  deleteReq,
};
